import { useState, useMemo } from 'react';
import { SortableHeader } from '@/components/glitch-ui/data-table/sortable-header';

type SortDirection = 'asc' | 'desc' | false;

export type InlineTableColumn<TData> = {
  id: string;
  header?: string;
  className?: string;
  sortable?: boolean;
  sortFn?: (a: TData, b: TData) => number;
  render: (row: TData) => React.ReactNode;
};

type InlineTableProps<TData> = {
  data: TData[];
  columns: InlineTableColumn<TData>[];
  rowKey: (row: TData) => string;
  emptyIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  emptyTitle?: string;
  emptyDescription?: string;
};

export function InlineTable<TData>({
  data,
  columns,
  rowKey,
  emptyIcon: EmptyIcon,
  emptyTitle,
  emptyDescription,
}: InlineTableProps<TData>) {
  const [sortColumnId, setSortColumnId] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(false);

  const handleSort = (columnId: string) => {
    if (sortColumnId !== columnId) {
      setSortColumnId(columnId);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortColumnId(null);
      setSortDirection(false);
    }
  };

  const sortedData = useMemo(() => {
    if (!sortColumnId || !sortDirection) return data;
    const column = columns.find((c) => c.id === sortColumnId);
    if (!column?.sortFn) return data;
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => multiplier * column.sortFn!(a, b));
  }, [data, columns, sortColumnId, sortDirection]);

  if (data.length === 0 && EmptyIcon && emptyTitle) {
    return (
      <div className='border rounded-lg bg-white overflow-hidden'>
        <div className='flex flex-col items-center justify-center py-16 text-center'>
          <EmptyIcon className='h-12 w-12 text-gray-500 mb-4' strokeWidth={1} />
          <h3 className='text-lg font-semibold text-gray-500 mb-2'>{emptyTitle}</h3>
          {emptyDescription && <p className='text-gray-500'>{emptyDescription}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className='border rounded-lg bg-white overflow-hidden'>
      <div className='overflow-x-auto'>
        <table className='min-w-full divide-y divide-gray-200'>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.id}
                  className={col.className ?? 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'}
                >
                  {col.header && col.sortable ? (
                    <SortableHeader
                      sortDirection={sortColumnId === col.id ? sortDirection : false}
                      onSort={() => handleSort(col.id)}
                    >
                      {col.header}
                    </SortableHeader>
                  ) : col.header ? (
                    <span className='text-xs font-medium text-gray-500 uppercase tracking-wider'>{col.header}</span>
                  ) : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='bg-white divide-y divide-gray-200'>
            {sortedData.map((row) => (
              <tr key={rowKey(row)} className='hover:bg-gray-50'>
                {columns.map((col) => (
                  <td key={col.id} className={col.className ?? 'px-6 py-4 whitespace-nowrap'}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
